import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { DashboardCardCP } from 'app/screens/screen-home/components/DashboardCardCP'
import { DashboardDefaultsUtils } from 'app/screens/screen-home/inner/DashboardDefaultsUtils'
import { DashboardFilterTP } from 'app/screens/screen-home/inner/DashboardFilterTP'
import { DashboardLinksUtils } from 'app/screens/screen-home/inner/DashboardLinksUtils'
import { ThemeProjectCommon } from 'config/theme/project/white-labels/ThemeProjectCommon'
import { GoogleMapCP } from 'modules/map/components/google-map/GoogleMapCP'
import { MapPinTP } from 'modules/map/components/google-map/inner/MapPinTP'
import { ChartHistoricalSeriesCP } from 'modules/reports/components/charts/chart-historical-series/ChartHistoricalSeriesCP'
import { ChartsRequests } from 'submodules/nerit-aquatec-sdk/services/charts/ChartsRequests'
import { GenericChartResponseDTO } from 'submodules/nerit-aquatec-sdk/services/charts/dtos/responses/GenericChartResponseDTO'
import { FieldRegulationRequests } from 'submodules/nerit-aquatec-sdk/services/regulation/FieldRegulationRequests'
import { EmptyCP } from 'submodules/nerit-framework-ui/common/components/empty/EmptyCP'
import { IDateRangeFilter } from 'submodules/nerit-framework-ui/common/components/form-fields/date-range-picker/inner/IDateRangeFilter'
import { SelectCP } from 'submodules/nerit-framework-ui/common/components/form-fields/select/SelectCP'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { TextCP } from 'submodules/nerit-framework-ui/common/components/text/TextCP'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { ISearchResult } from 'submodules/nerit-framework-utils/sdk-utils/dtos/interfaces/ISearchResult'
import { NameAndCodeResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/NameAndCodeResponseDTO'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'

/** A série mostra os 12 meses anteriores à data final do filtro, para evidenciar tendência. */
const HISTORIC_SERIES_MONTHS = 12

interface ICPProps {
    filter: DashboardFilterTP
}

/**
 * Relação espacial e temporal: mapa dos pontos monitorados e série histórica do ponto selecionado.
 */
export function DashboardSpatialTemporalCP(props: ICPProps): JSX.Element {

    const [fields, setFields] = useState<NameAndCodeResponseDTO[]>([])
    const [fieldCode, setFieldCode] = useState<number>()
    const [selectedLocationCode, setSelectedLocationCode] = useState<number>()
    const [historicData, setHistoricData] = useState<GenericChartResponseDTO>()
    const [isLoadingHistoric, setIsLoadingHistoric] = useState<boolean>(false)

    /* Ao trocar ponto/parâmetro rapidamente, só a última requisição da série é considerada. */
    const historicRequestIdRef = useRef<number>(0)

    const mappableLocations = useMemo(
        () => props.filter.locations
            .filter((location) => !Number.isNaN(parseFloat(location.latitude)) && !Number.isNaN(parseFloat(location.longitude)))
            .sort((a, b) => a.name.localeCompare(b.name)),
        [props.filter],
    )

    const historicDateRange = useMemo(() => getHistoricDateRange(props.filter.dateRange), [props.filter])

    useEffect(() => { void loadFields() }, [])
    useEffect(() => setSelectedLocationCode(mappableLocations[0]?.code), [mappableLocations])
    useEffect(() => { void loadHistoric() }, [fieldCode, selectedLocationCode, props.filter])

    /**
     */
    async function loadFields(): Promise<void> {

        try {
            const response = await RequestHelper.runRequest<IApiReturn<ISearchResult<NameAndCodeResponseDTO>>>(FieldRegulationRequests.getFieldList())
            // A lista vem com o mesmo parâmetro repetido (um por norma); mantemos um por código.
            const list = Array.from(new Map((response.data.data?.list ?? []).map((field) => [field.code, field])).values())
            setFields(list)
            setFieldCode(DashboardDefaultsUtils.findDefaultField(list)?.code ?? list[0]?.code)
        } catch {
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        }
    }

    /**
     */
    async function loadHistoric(): Promise<void> {

        if (!fieldCode || !selectedLocationCode) {
            setHistoricData(undefined)
            return
        }

        const requestId = ++historicRequestIdRef.current
        setIsLoadingHistoric(true)
        try {
            const response = await RequestHelper.runRequest<IApiReturn<GenericChartResponseDTO>>(
                ChartsRequests.getHistoricChart({
                    fieldCodes: [fieldCode],
                    locationCode: selectedLocationCode,
                    regulationCode: props.filter.regulationCode,
                    dateRange: historicDateRange,
                }),
            )
            if (requestId === historicRequestIdRef.current)
                setHistoricData(response.data.data)
        } catch {
            if (requestId === historicRequestIdRef.current) {
                setHistoricData(undefined)
                NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
            }
        } finally {
            if (requestId === historicRequestIdRef.current)
                setIsLoadingHistoric(false)
        }
    }

    const mapPins: MapPinTP[] = mappableLocations.map((location) => ({
        id: location.code,
        position: { lat: parseFloat(location.latitude), lng: parseFloat(location.longitude) },
        title: location.name,
        icon: location.code === selectedLocationCode ? ThemeProjectCommon.mapMarkers.red : ThemeProjectCommon.mapMarkers.blue,
        onClick: () => setSelectedLocationCode(location.code),
    }))

    const selectedLocation = mappableLocations.find((location) => location.code === selectedLocationCode)
    const hasHistoricValues = !!historicData && historicData.series.some((serie) => serie.dataArray.length > 0)

    return (
        <RowCP>
            <ColumnCP size={12} xs={24} lg={12}>
                <DashboardCardCP
                    title={'Pontos Monitorados'}
                    subtitle={`${mappableLocations.length} pontos · clique em um ponto para atualizar a série`}
                    minHeight={480}
                    link={{
                        label: 'Abrir mapa',
                        onClick: () => DashboardLinksUtils.goToMap(props.filter, fieldCode),
                    }}
                >
                    {
                        mappableLocations.length > 0
                            ? (
                                <MapWrapperSCP>
                                    <GoogleMapCP key={props.filter.samplingCode} markers={mapPins}/>
                                </MapWrapperSCP>
                            )
                            : <EmptyCP description={'Nenhum ponto da campanha possui coordenadas'}/>
                    }
                </DashboardCardCP>
            </ColumnCP>

            <ColumnCP size={12} xs={24} lg={12}>
                <DashboardCardCP
                    title={'Série Histórica'}
                    subtitle={!!selectedLocation ? `Ponto ${selectedLocation.name} · últimos ${HISTORIC_SERIES_MONTHS} meses` : undefined}
                    isLoading={isLoadingHistoric}
                    minHeight={480}
                    extra={
                        <SelectCP
                            label={'Parâmetro'}
                            width={240}
                            value={fieldCode}
                            filterOption={true}
                            options={fields.map((field) => ({ value: field.code, label: field.name }))}
                            onChange={(code: number) => setFieldCode(code)}
                        />
                    }
                    link={{
                        label: 'Abrir análise',
                        onClick: () => DashboardLinksUtils.goToHistoricSeries(props.filter, fieldCode!, selectedLocationCode!, historicDateRange),
                        disabled: !fieldCode || !selectedLocationCode,
                    }}
                >
                    {
                        hasHistoricValues
                            ? (
                                <ChartWrapperSCP>
                                    <ChartHistoricalSeriesCP
                                        data={historicData!}
                                        filters={{ locationName: selectedLocation?.name ?? '', regulationName: props.filter.regulationName }}
                                        appearance={{ hideTitle: true }}
                                    />
                                </ChartWrapperSCP>
                            )
                            : !isLoadingHistoric && <TextCP text={'Sem resultados do parâmetro para este ponto no período.'} color={'#888'} wrap={true}/>
                    }
                </DashboardCardCP>
            </ColumnCP>
        </RowCP>
    )
}

function getHistoricDateRange(dateRange: IDateRangeFilter): IDateRangeFilter {
    const endDate = dateRange.endDate ?? new Date()
    return {
        beginDate: moment(endDate).subtract(HISTORIC_SERIES_MONTHS, 'months').startOf('day')
            .toDate(),
        endDate,
    }
}

const MapWrapperSCP = styled.div`
    height: 400px;
    position: relative;
`

/** O gráfico reaproveitado já vem dentro de um card; aqui removemos a borda/margem para não duplicar. */
const ChartWrapperSCP = styled.div`
    .ant-card {
        margin: 0;
        border: none;
    }
`
